import { useEffect, useRef, useState } from 'react';

import placeholderImg from '@assets/verktoyplaceholder400x400.png';
import VerktoyTags from '@components/tags-component/tags-component';
import AddVerktoyElement from '@components/verktoy-liste/add-verktoy-element';
import { useGetResources } from '@hooks/useGetResources';
import { useLogin } from '@hooks/useLogin';
import { VERKTOY_PROXY_NAME } from '@src/types/apiTypes';
import { GrafiskElementType, type MittVerktoyItem, type Verktoy, type LenkeType } from '@src/types/minhelseentities';
import { TagGroup } from '@src/types/types';
import { useAppSelector } from '@store';
import { ApiURL } from '@store/constants/apiURLs';
import { kanAnonymBrukerSeAapneKnapp, kanInnloggetBrukerSeAapneKnapp, maaLoggeInnForAaAapneVerktoy } from '@utils/aapneVerktoy-utils';
import { sporAapningAvVerktoyFraLenke, sporSettVerktoyside } from '@utils/amplitude-utils';
import { launchAktivitet } from '@utils/launch-aktivitet';
import { getOppgaveFlowState } from '@utils/oppgave-flow-utils';
import { getVerktoyPersonvernTekst } from '@utils/personvern';
import { erVerktoyTilgjengelig, trackAnalyticsBasertPaaLenkeType } from '@utils/utils';

import HelpBubble from '@helsenorge/designsystem-react/components/HelpBubble';
import HelpQuestion from '@helsenorge/designsystem-react/components/HelpTriggerStandalone';
import Modal from '@helsenorge/designsystem-react/components/Modal';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Title from '@helsenorge/designsystem-react/components/Title';
import designsystemtypography from '@helsenorge/designsystem-react/scss/typography.module.scss';

import SafeHTML from '@helsenorge/framework-utils/components/safe-html';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { link } from '@helsenorge/framework-utils/hn-proxy-service';

import OpenVerktoyButtons, { ButtonsVisibility } from './components/buttons/open-verktoy-buttons';
import { MarkdownContent, ExternalLinkRenderer } from './components/content/markdownContent';
import LaunchUserInfo from './components/info/launch-userinfo';
import OpenUtvidetInfoLink from './components/info/open-utvidetinfo-link';
import VerktoyInfoElement from './components/info/verktoy-info-element';

interface VerktoyDetailsPageProps {
  verktoy: Verktoy;
  startAktivitet?: () => void;
}

const VerktoyDetailsPage = ({ verktoy, startAktivitet }: VerktoyDetailsPageProps): React.JSX.Element => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isPersonvernBubbleOpen, setIsPersonvernBubbleOpen] = useState(false);

  const resources = useGetResources();

  const isWaitingForAktivitetResponse = useAppSelector(globalState => globalState.verktoy.isWaitingForAktivitetResponse);
  const isOppgaveEnrollmentSuccess = useAppSelector(globalState => globalState.verktoy.isOppgaveEnrollmentSuccess);
  const mineVerktoyListe = useAppSelector(globalState => globalState.verktoy.mineVerktoyListe);
  const oppgaveFlow = getOppgaveFlowState();

  const erLaeringsaktivitet = mineVerktoyListe.some(
    (item: MittVerktoyItem) =>
      item.VerktoyId === verktoy.VerktoyId && item.LaeringsaktivitetId === verktoy.Laeringsaktivitet?.LaeringsaktivitetId
  );

  const topElementRef = useRef<HTMLDivElement | null>(null);
  const personvernButtonRef = useRef<HTMLButtonElement>(null);
  const openVerktoyButtonRef = useRef<HTMLButtonElement>(null);
  const trackedVerktoyIdRef = useRef<string | undefined>(undefined);
  const { login } = useLogin();
  const ikon = verktoy.GrafiskeElementer.find(x => x.TypeId === GrafiskElementType.Ikon);
  const bilde = verktoy.GrafiskeElementer.filter(x => x.TypeId === GrafiskElementType.Bilde);
  const erNasjonaltVerktoy = verktoy.Laeringsressurs?.KreverForskrivning === false;
  const harGyldigLaeringsaktivitet = erLaeringsaktivitet && verktoy.Laeringsaktivitet?.LaeringsaktivitetId !== undefined;
  const deletedAfterOppgaveAccept = isOppgaveEnrollmentSuccess && !harGyldigLaeringsaktivitet;
  const shouldHideStartButtons =
    (oppgaveFlow.inOppgaveFlow && !isOppgaveEnrollmentSuccess) || (deletedAfterOppgaveAccept && !erNasjonaltVerktoy);
  const shouldHideLaunchUserInfo = oppgaveFlow.inOppgaveFlow && !isOppgaveEnrollmentSuccess;

  const shouldShowOmPersonvernHjelp =
    (kanInnloggetBrukerSeAapneKnapp(verktoy, erLaeringsaktivitet) && verktoy.Lenker) ||
    (kanAnonymBrukerSeAapneKnapp(verktoy) && verktoy.Lenker);

  const startVerktoyAnonym = (lenkeType: LenkeType): void => {
    if (maaLoggeInnForAaAapneVerktoy(verktoy)) {
      setShowLoginModal(true);
    } else {
      startVerktoy(lenkeType);
    }
  };

  const startVerktoy = (lenkeType: LenkeType): void => {
    if (startAktivitet) {
      startAktivitet();
    }
    trackAnalyticsBasertPaaLenkeType(lenkeType, verktoy.Navn);
    sporAapningAvVerktoyFraLenke(verktoy, lenkeType);
    launchAktivitet({
      verktoyId: verktoy.VerktoyId,
      oppstartsType: verktoy.OppstartType,
      lenker: verktoy.Lenker,
      lenkeType: lenkeType,
      kanStartesAvInnloggetBruker: verktoy.KanStartesAvInnloggetBruker,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (topElementRef.current) {
      topElementRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (trackedVerktoyIdRef.current === verktoy.VerktoyId) {
      return;
    }
    trackedVerktoyIdRef.current = verktoy.VerktoyId;
    sporSettVerktoyside(verktoy);
  }, [verktoy]);

  return (
    <>
      {isOppgaveEnrollmentSuccess && !isWaitingForAktivitetResponse && erLaeringsaktivitet && (
        <div className="verktoy__wrapper">
          <NotificationPanel testId="notification-success-enrolled" variant="success">
            {resources?.enroll_ok_tekst.replace('{0}', verktoy.Navn)}
          </NotificationPanel>
        </div>
      )}
      <div ref={topElementRef} tabIndex={-1} className="details__heading_container">
        <div>
          <Title className="details__title" appearance="title1" htmlMarkup="h1">
            {verktoy.Navn}
          </Title>
          <div className="details__ingress" lang="nb-NO">
            {verktoy.Ingress}
          </div>
        </div>
        <div>
          {ikon ? (
            <div>
              <img
                className="verktoy__icon"
                alt={ikon.AlternativTekst}
                src={link(VERKTOY_PROXY_NAME, ApiURL.ANONYM_GRAFISKELEMENT, { id: ikon.Id })}
              />
            </div>
          ) : (
            <div>
              <img className="verktoy__icon" id="defaulticon" alt="defaultverktoyicon" src={placeholderImg} />
            </div>
          )}
        </div>
      </div>

      {!shouldHideLaunchUserInfo && <LaunchUserInfo erLaeringsaktivitet={erLaeringsaktivitet} verktoy={verktoy} showOnDetailsPage={true} />}

      <div className="details__buttons_container">
        {!shouldHideStartButtons && isAuthorized() && kanInnloggetBrukerSeAapneKnapp(verktoy, erLaeringsaktivitet) && verktoy.Lenker && (
          <OpenVerktoyButtons
            verktoyLenker={verktoy.Lenker}
            buttonRef={openVerktoyButtonRef}
            startVerktoy={startVerktoy}
            visibility={ButtonsVisibility.ShowAll}
          />
        )}

        {!shouldHideStartButtons && isAuthorized() === false && kanAnonymBrukerSeAapneKnapp(verktoy) && verktoy.Lenker && (
          <OpenVerktoyButtons
            verktoyLenker={verktoy.Lenker}
            buttonRef={openVerktoyButtonRef}
            startVerktoy={startVerktoyAnonym}
            visibility={ButtonsVisibility.ShowAll}
          />
        )}

        {shouldShowOmPersonvernHjelp && (
          <div className="details__button_flexWidth">
            <div>
              <HelpQuestion
                testId="om-personvern-hjelpe"
                ref={personvernButtonRef}
                aria-expanded={isPersonvernBubbleOpen}
                aria-haspopup="true"
                onClick={(): void => setIsPersonvernBubbleOpen(!isPersonvernBubbleOpen)}
              >
                {resources?.omPersonvernButton || ''}
              </HelpQuestion>
            </div>
          </div>
        )}
        <HelpBubble
          controllerRef={personvernButtonRef as React.RefObject<HTMLElement>}
          showBubble={isPersonvernBubbleOpen}
          onClose={(): void => setIsPersonvernBubbleOpen(false)}
          variant="positionbelow"
        >
          <VerktoyInfoElement label={resources?.personvernLabel}>
            <SafeHTML
              className={`${designsystemtypography['anchorlink-wrapper']} dd--styling-netrual`}
              html={getVerktoyPersonvernTekst(resources, verktoy)}
              tagName="dd"
            />
          </VerktoyInfoElement>
        </HelpBubble>
        {!shouldHideStartButtons && (
          <div>
            <AddVerktoyElement verktoy={verktoy} erLaeringsaktivitet={erLaeringsaktivitet} />
          </div>
        )}
      </div>

      {bilde.length > 0 && erVerktoyTilgjengelig(verktoy) && (
        <img
          className="details__image"
          alt={bilde[0].AlternativTekst}
          src={link(VERKTOY_PROXY_NAME, ApiURL.ANONYM_GRAFISKELEMENT, { id: bilde[0].Id })}
        />
      )}

      <div className="details__omVerktoy_container">
        <Title appearance="title2" htmlMarkup="h2">
          {resources?.verktoy_details_om_verktoyet_header}
        </Title>
        <MarkdownContent
          components={{
            a(props) {
              return (
                <ExternalLinkRenderer href={props.href} ariaLabel={resources?.verktoy_details_markdown_aria_label_lenke}>
                  {props.children}{' '}
                </ExternalLinkRenderer>
              );
            },
          }}
        >
          {verktoy.Beskrivelse}
        </MarkdownContent>
      </div>

      <div className="details__utvidetInfo_container">
        <VerktoyInfoElement label={''}>
          <OpenUtvidetInfoLink verktoyLenker={verktoy.Lenker} verktoyNavn={verktoy.Navn} interntVerktoy={verktoy.InterntVerktoy} />
        </VerktoyInfoElement>
      </div>
      <div className="details__informasjon_container">
        <Title appearance="title2" htmlMarkup="h2">
          {resources?.verktoydetails_informasjon_header}
        </Title>
      </div>

      <div>
        <VerktoyTags verktoy={verktoy} tagGroup={TagGroup.detailTags} />
      </div>

      {showLoginModal && (
        <>
          <Modal
            title={resources?.openButtonText || 'Åpne verktøy'}
            description={resources?.verktoyElement_loginMessage}
            primaryButtonText={resources?.modalLoginButtonText}
            secondaryButtonText={resources?.modalCancelButtonText}
            onSuccess={(): void => {
              login(openVerktoyButtonRef);
            }}
            onClose={(): void => setShowLoginModal(false)}
          />
        </>
      )}
    </>
  );
};

export default VerktoyDetailsPage;
